import React, {useEffect, useState} from 'react';
import {useDispatch, useSelector} from 'react-redux';
import {useLocation} from 'react-router-dom';
import queryString from 'query-string';

import {labels, currentResource} from '../../../../util/utils';

import {assertionOrder, groupRiskStatus, reconciliationsFilterIds, routes} from '../../../../util/uiconstants';

import selectSelectedReconciliation from '../../../../reducers/Reconciliations/selectors/selectSelectedReconciliation';
import selectFilteredDataList from '../../../../reducers/Reconciliations/selectors/selectFilteredDataList';
import selectUnmappedComponentAccounts from '../../../../reducers/Reconciliations/selectors/selectUnmappedComponentAccounts';
import selectComponentAccounts from '../../../../reducers/Reconciliations/selectors/selectComponentAccounts';
import selectComponentRisks from '../../../../reducers/Reconciliations/selectors/selectComponentRisks';
import selectComponentEngagement from './../../../../reducers/Reconciliations/selectors/selectComponentEngagement';
import {
	clearSelectedReconciliation,
	getReconciliationAccounts,
	getReconciliationRisks,
	updateReconciliationAccount
} from '../../../../actions/Reconciliations';
import {getEngagementMetaData} from '../../../../actions/Engagement/engagementactions';

import OutputText from '@ey/canvascoreservices/OutputText';

import TemplateFullPage from '../../../Templates/TemplateFullPage';
import Breadcrumb from '../../../CoreWrappers/Breadcrumb';
import AccountDetails from '../../../Common/AccountDetails';
import {StyledAccountDetailsGridHeader} from '../../../Common/AccountDetails/StyledAccountDetailsGrid';
import RiskDetails from '../../../Common/RiskDetails';
import ListWithScroll from '../../../Common/ListWithScroll';
import PanelSelector from '../../../Common/PanelSelector';
import ExportWithTopDetails from '../../../Common/ExportToExcel/ExportWithTopDetails';
import ReconciliationsGenerateMail from '../../../Common/ReconciliationsGenerateMail';
import AccountAndRiskDetailsFooterMessage from '../../../Common/AccountAndRiskDetailsFooterMessage';
import Badge from '../../../CoreWrappers/Badge';

import {generateExcelWithDetailsOnTop} from './utils/generateExcelWithDetailsOnTop';
import {
	getColumnsToExport as getColumnsToExportAccount,
	getDataToExport as getDataToExportAccount
} from './utils/getColumnsToExport';
import {getColumnsToExportRisk, getDataToExportRisk} from './utils/reconciliationExportRisks';

import StyledComponentView from './StyledComponentView';
import StyledSelectorPanels from './StyledSelectorPanels';
import StyledGenerateMail from './StyledGenerateMail';
import {SUCCESS_MESSAGE} from '../../../../actions/actiontypes';
import ComponentAccountsModal from './ComponentAccountsModal';
import UnmappedRisksModal from './UnmappedRisksModal';
import selectUnmappedComponentRisks from '../../../../reducers/Reconciliations/selectors/selectUnmappedComponentRisks';
import {MIDDLE_FILE_NAME} from './utils/constants';
import selectProjectAndEngIds from '../../../../reducers/Reconciliations/selectors/selectProjectAndEngIds';

const ComponentView = () => {
	const [isLoading, setIsLoading] = useState(false);
	const {openAccounts, assessedAccounts, openRisks, assessedRisks} = reconciliationsFilterIds;
	const [filterValue, setFilterValue] = useState(openAccounts);
	const project = useSelector((store) => store.project);
	const engagementMetaData = useSelector(({engagementMetaData}) => engagementMetaData);
	const userPreferences = useSelector(({userPreferences}) => userPreferences);

	const exportToExcelLabels = currentResource.exportToExcelLabels;

	const {
		componentName,
		engagementId,
		engagementDescription,
		openAccountsCount,
		assessedAccountsCount,
		openRisksCount,
		assessedRisksCount
	} = selectSelectedReconciliation();

	const componentAccounts = selectComponentAccounts();
	const componentRisks = selectComponentRisks();
	const unmappedComponentAccounts = selectUnmappedComponentAccounts();
	const hasUnmmapedComponentAccounts = unmappedComponentAccounts.length > 0;
	const hasUnmmapedComponentRisks = selectUnmappedComponentRisks().length > 0;
	const apiConfig = selectProjectAndEngIds();
	
	const accountItems = [
		{
			id: openAccounts,
			label: labels.openAccounts,
			count: openAccountsCount,
			isAccounts: true,
			riskStatusId: [groupRiskStatus.open, groupRiskStatus.systemReOpened]
		},
		{
			id: assessedAccounts,
			label: labels.assessedAccounts,
			count: assessedAccountsCount,
			isAccounts: true,
			riskStatusId: [groupRiskStatus.assessed]
		}
	];

	const riskItems = [
		{
			id: openRisks,
			label: labels.openRisks,
			count: openRisksCount,
			isAccounts: false,
			riskStatusId: [groupRiskStatus.open, groupRiskStatus.systemReOpened]
		},
		{
			id: assessedRisks,
			label: labels.assessedRisks,
			count: assessedRisksCount,
			isAccounts: false,
			riskStatusId: [groupRiskStatus.assessed]
		}
	];

	const filterItems = [...accountItems, ...riskItems];

	const {isAccounts, riskStatusId, label: headingLabel} = filterItems.find(({id}) => filterValue === id);

	const data = selectFilteredDataList(isAccounts, riskStatusId);

	const dispatch = useDispatch();
	const location = useLocation();
	const {componentId = null, projectId = null} = queryString.parse(location.search);

	
	useEffect(() => {
		if (projectId && componentId) handleGetComponentData();
		if(engagementMetaData === null) dispatch(getEngagementMetaData(projectId, userPreferences?.userType));
		return () => {
			dispatch(clearSelectedReconciliation());
		};
	}, [projectId, componentId]);
	
	if (!componentId || !projectId) return false;
	
	const componentEngagement = selectComponentEngagement(componentId);

	const handleGetComponentData = () => {
		setIsLoading(true);
		Promise.all([
			dispatch(getReconciliationAccounts(componentId, apiConfig)),
			dispatch(getReconciliationRisks(componentId, apiConfig))
		]).finally(() => setIsLoading(false));
	};

	const handleGroupRiskStatusChange = (primaryAccountId, groupRiskStatusId) => {
		const payload = {
			groupRiskStatusId: groupRiskStatusId
		};

		setIsLoading(true);
		dispatch(updateReconciliationAccount(primaryAccountId, payload, apiConfig))
			.then(() => {
				const successMessage =
					groupRiskStatusId === groupRiskStatus.assessed
						? labels.markAssessedSuccessMessage
						: labels.reopenSuccessMessage;
				dispatch({type: SUCCESS_MESSAGE, message: successMessage});
			})
			.finally(() => {
				setIsLoading(false);
			});
	};

	const renderBreadcrumb = () => {
		const groupRiskAssessmentUrl = `${routes.gra.root}?${routes.queryString.projectId}=${projectId}${location.hash}`;

		const breadcrumbs = [
			{id: '1', label: labels.groupRiskAssessment, url: groupRiskAssessmentUrl},
			{id: '2', label: labels.reconciliations},
			{id: '3', label: componentName || ''}
		];

		const props = {
			items: breadcrumbs
		};

		return <Breadcrumb {...props} />;
	};

	const renderEngagementUsersTooltip = () => {
		const props = {
			engagementId
		};

		const date = new Date().toISOString().split('T')[0];

		const getExportDetails = () => {
			const title = isAccounts
				? exportToExcelLabels.reconciliationComponentExportTabTitle
				: exportToExcelLabels.reconciliationExportTabRisks;
			const getColumnsToExport = isAccounts
				? getColumnsToExportAccount(exportToExcelLabels)
				: getColumnsToExportRisk(exportToExcelLabels);
			const getDataToExport = isAccounts
				? getDataToExportAccount(data, componentAccounts)
				: getDataToExportRisk(data, componentRisks);

			return {
				projectName: project.name,
				fileName: `${componentName}${MIDDLE_FILE_NAME}${date}`,
				title: title,
				columns: getColumnsToExport,
				data: getDataToExport,
				worksheetName: title,
				primaryTeamEngagementName: engagementDescription,
				componentTeamEngagementName: componentEngagement.description
			};
		};

		//end of export added code
		const prop = {
			getDataToExport: getExportDetails,
			buttonTitle: exportToExcelLabels.risks.exportButtonTitle,
			labels: exportToExcelLabels,
			generateTopDetails: generateExcelWithDetailsOnTop,
			isAccountExport: isAccounts
		};

		const outPutProps = {
			className: 'component-view-pipe',
			children: '|'
		};

		return (
			<StyledGenerateMail>
				<ReconciliationsGenerateMail {...props}></ReconciliationsGenerateMail>
				<OutputText {...outPutProps} />
				<ExportWithTopDetails {...prop} className="export-data" />
			</StyledGenerateMail>
		);
	};

	const renderSelectors = () => {
		const sumCounts = (acc, {count}) => acc + count;

		const getTotalItem = (label, count) => ({
			id: 'total',
			label,
			count,
			inactive: true
		});

		const accountProps = {
			title: labels.primaryTeamAccountStatus,
			description: labels.accountDescription,
			items: [
				getTotalItem(labels.totalAccounts, accountItems.reduce(sumCounts, 0)),
				{separator: true},
				...accountItems
			],
			value: accountItems.some((i) => i.id === filterValue) ? filterValue : null,
			onClick: (value) => setFilterValue(value)
		};

		const riskProps = {
			title: labels.primaryTeamRiskStatus,
			description: labels.riskDescription,
			items: [getTotalItem(labels.totalRisks, riskItems.reduce(sumCounts, 0)), {separator: true}, ...riskItems],
			value: riskItems.some((i) => i.id === filterValue) ? filterValue : null,
			onClick: (value) => setFilterValue(value)
		};

		const showModals = hasUnmmapedComponentAccounts || hasUnmmapedComponentRisks;

		return (
			<StyledSelectorPanels>
				<PanelSelector {...accountProps} />
				<PanelSelector {...riskProps} />
				{showModals && (
					<>
						<div>{hasUnmmapedComponentAccounts && <ComponentAccountsModal />}</div>
						<div>{hasUnmmapedComponentRisks && <UnmappedRisksModal />}</div>
					</>
				)}
			</StyledSelectorPanels>
		);
	};

	const renderAccountHeader = () => (
		<StyledAccountDetailsGridHeader>
			<OutputText h6 ellipsis title={labels.primaryTeamAccountName}>
				{labels.primaryTeamAccountName}
			</OutputText>
			<OutputText h6 ellipsis title={labels.pspIndexes} align="right">
				{labels.pspIndexes}
			</OutputText>
			<OutputText h6 ellipsis title={labels.designation}>
				{labels.designation}
			</OutputText>
			{assertionOrder.map((item) => (
				<OutputText h6 align="center" ellipsis title={item}>
					{item}
				</OutputText>
			))}
		</StyledAccountDetailsGridHeader>
	);

	const renderAccounts = (account) => {
		const props = {
			key: account.accountId,
			primaryAccount: account,
			componentAccounts: componentAccounts,
			onGroupRiskStatusChange: handleGroupRiskStatusChange
		};

		return <AccountDetails {...props} />;
	};

	const renderRiskHeader = () => <OutputText h6>{labels.primaryTeamRiskDesignationAndName}</OutputText>;

	const renderRisks = (risk) => {
		const props = {
			key: risk.riskId,
			primaryRisk: risk
		};

		return <RiskDetails {...props} />;
	};

	const renderList = () => {
		const props = {
			list: data,
			enableScroll: false
		};

		if (isAccounts) {
			props.item = renderAccounts;
			props.header = renderAccountHeader;
		} else {
			props.item = renderRisks;
			props.header = renderRiskHeader;
		}

		return <ListWithScroll {...props} />;
	};

	return (
		<TemplateFullPage isFlex={false}>
			<StyledComponentView isLoading={isLoading}>
				{renderBreadcrumb()}
				{data !== null && (
					<>
						<OutputText h2>{componentName}</OutputText>
						<OutputText light>{labels.reconciliationDetailInstructional}</OutputText>
						{renderSelectors()}
						{renderEngagementUsersTooltip()}
						<OutputText h5>{headingLabel}</OutputText>
						{renderList()}
					</>
				)}
			</StyledComponentView>
		</TemplateFullPage>
	);
};

ComponentView.propTypes = {};

export default ComponentView;
